import { useEffect, useState } from "react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api, formatErr } from "../lib/api";
import { useAuth, canAtLeast } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Target, Trash2, Pencil, X, RefreshCw, Loader2 } from "lucide-react";
import { compact, formatNum } from "../lib/format";

const AGGREGATIONS = ["sum", "avg", "count", "count_distinct", "min", "max"];

export default function KpiCenter() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [values, setValues] = useState({}); // id -> value
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [k, d] = await Promise.all([api.get("/kpis"), api.get("/datasets")]);
      setKpis(k.data.kpis || []);
      setDatasets([...(d.data.datasets || []), ...(d.data.demo_datasets || [])]);
      // run all
      const results = await Promise.all((k.data.kpis || []).map((kp) => api.post(`/kpis/${kp.id}/run`).then((r) => [kp.id, r.data]).catch(() => [kp.id, null])));
      const v = {}; results.forEach(([id, data]) => { if (data) v[id] = data.value; });
      setValues(v);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const datasetName = (id) => datasets.find((d) => (d.id || `demo:${d.demo_key}`) === id)?.name || id;

  const remove = async (id, name) => {
    if (!confirm(`Delete KPI "${name}"?`)) return;
    try { await api.delete(`/kpis/${id}`); toast.success("KPI deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const refresh = async (id) => {
    try { const r = await api.post(`/kpis/${id}/run`); setValues((s) => ({ ...s, [id]: r.data.value })); toast.success("Refreshed"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>KPI Center</h1>
            <p>Curated, refreshable business metrics for analytics dashboards and reports.</p>
          </div>

          <div className="flex items-center gap-2">
            <button className="btn btn-outline" onClick={load} disabled={loading}>
              <RefreshCw size={14} strokeWidth={1.8} />
              {loading ? "Refreshing…" : "Refresh"}
            </button>

            {canAtLeast(user, "analyst") && (
              <button className="erp-primary-btn" onClick={() => setShowCreate(true)} data-testid="kpi-new">
                <Plus size={14} strokeWidth={1.8} /> Create KPI
              </button>
            )}
          </div>
        </div>

        <div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {[0,1,2,3].map((i) => <div key={i} className="card-flat p-4 skeleton" style={{ height: 110 }} />)}
            </div>
          ) : kpis.length === 0 ? (
            <div className="card-flat p-10 text-center text-[13px]" style={{ color: "var(--c-text-muted)" }}>
              No KPIs yet. {canAtLeast(user, "analyst") && 'Click "Create KPI" to define your first metric.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="kpi-grid">
              {kpis.map((k) => {
                const v = values[k.id];
                return (
                  <div key={k.id} className="card-flat p-4 flex flex-col" data-testid={`kpi-card-${k.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="kpi-label truncate">{k.name}</div>
                        <div className="text-[10.5px] mt-0.5" style={{ color: "var(--c-text-muted)" }}>{datasetName(k.dataset_id)}</div>
                      </div>
                      <Target size={16} strokeWidth={1.7} className="opacity-50 shrink-0" />
                    </div>
                    <div className="kpi-value mt-2">{v !== undefined ? compact(v) : <Loader2 size={16} className="animate-spin" />}</div>
                    <div className="text-[11px] numeric mt-0.5" style={{ color: "var(--c-text-muted)" }}>{v !== undefined ? formatNum(v, k.number_format || {}) : "—"}</div>
                    <div className="text-[10.5px] mt-1" style={{ color: "var(--c-text-muted)" }}>
                      {k.aggregation} · {k.measure || "rows"}
                    </div>
                    <div className="mt-auto pt-3 flex gap-1 justify-end">
                      <button className="btn btn-ghost" title="Refresh" onClick={() => refresh(k.id)}><RefreshCw size={12} strokeWidth={1.8} /></button>
                      {canAtLeast(user, "analyst") && <button className="btn btn-ghost" title="Edit" onClick={() => setEditing(k)}><Pencil size={12} strokeWidth={1.7} /></button>}
                      {canAtLeast(user, "analyst") && <button className="btn btn-ghost" title="Delete" onClick={() => remove(k.id, k.name)} data-testid={`kpi-del-${k.id}`}><Trash2 size={12} strokeWidth={1.7} /></button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {showCreate && <KpiModal datasets={datasets} onClose={() => { setShowCreate(false); load(); }} />}
        {editing && <KpiModal datasets={datasets} existing={editing} onClose={() => { setEditing(null); load(); }} />}
      </main>
    </div>
  );
}

function KpiModal({ datasets, existing, onClose }) {
  const isEdit = !!existing;
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [form, setForm] = useState({
    name: existing?.name || "",
    dataset_id: existing?.dataset_id || "",
    measure: existing?.measure || "",
    aggregation: existing?.aggregation || "sum",
    filters: existing?.filters || [],
    number_format: existing?.number_format || {},
    description: existing?.description || "",
    workspace_id: existing?.workspace_id || user?.workspace_id || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.role === "super_admin") {
      api.get("/workspaces").then((r) => setWorkspaces(r.data.workspaces || []));
    }
  }, [user]);

  const update = (p) => { setForm((s) => ({ ...s, ...p })); setError(""); };
  const ds = datasets.find((d) => (d.id || `demo:${d.demo_key}`) === form.dataset_id);
  const allFields = ds?.fields || [];

  // Auto-resolve workspace from the picked dataset (live datasets carry workspace_id).
  useEffect(() => {
    if (form.workspace_id) return;
    if (!ds) return;
    if (ds.workspace_id) update({ workspace_id: ds.workspace_id });
    else if (user?.role !== "super_admin" && user?.workspace_id) update({ workspace_id: user.workspace_id });
  }, [form.dataset_id, ds, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const camarooDefault = workspaces.find((w) => w.slug === "camaroo");
  const effectiveWorkspace = form.workspace_id || (user?.role === "super_admin" ? camarooDefault?.id : user?.workspace_id);

  const save = async () => {
    setError("");
    if (!form.name?.trim()) { setError("Name required"); return; }
    if (!form.dataset_id) { setError("Pick a dataset"); return; }
    setSaving(true);
    try {
      const payload = { ...form, workspace_id: effectiveWorkspace || undefined };
      if (isEdit) await api.put(`/kpis/${existing.id}`, payload);
      else await api.post("/kpis", payload);
      toast.success(isEdit ? "KPI updated" : "KPI saved");
      onClose();
    } catch (e) { const m = formatErr(e); setError(m); toast.error(m); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30" data-testid="kpi-modal">
      <div className="bg-white w-[520px] max-w-[95vw] rounded-md border" style={{ borderColor: "var(--c-border)" }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--c-border)" }}>
          <div className="section-title">{isEdit ? `Edit · ${existing.name}` : "Create KPI"}</div>
          <button className="btn btn-ghost" onClick={onClose}><X size={14} strokeWidth={1.8} /></button>
        </div>
        <div className="p-5 space-y-3">
          {user?.role === "super_admin" && (
            <div>
              <label className="label">Workspace</label>
              <select className="select" value={form.workspace_id || ""} onChange={(e) => update({ workspace_id: e.target.value })} data-testid="kpi-workspace">
                <option value="">{ds?.workspace_id ? "Auto (from dataset)" : "— pick workspace —"}</option>
                {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
              {!form.workspace_id && effectiveWorkspace && (
                <div className="text-[11px] mt-1" style={{ color: "var(--c-text-muted)" }}>
                  Will save in workspace <span className="font-medium" style={{ color: "var(--c-text)" }}>{(workspaces.find((w) => w.id === effectiveWorkspace) || {}).name || effectiveWorkspace}</span>
                </div>
              )}
            </div>
          )}
          {user?.role !== "super_admin" && user?.workspace_id && (
            <div className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>
              Workspace: <span className="font-medium" style={{ color: "var(--c-text)" }}>{user.workspace_id}</span>
            </div>
          )}
          <div>
            <label className="label">Name</label>
            <input className="input" value={form.name} onChange={(e) => update({ name: e.target.value })} data-testid="kpi-name" />
          </div>
          <div>
            <label className="label">Dataset</label>
            <select className="select" value={form.dataset_id} onChange={(e) => update({ dataset_id: e.target.value, measure: "" })} data-testid="kpi-ds">
              <option value="">— pick dataset —</option>
              {datasets.map((d) => <option key={d.id || `demo:${d.demo_key}`} value={d.id || `demo:${d.demo_key}`}>{d.name}{d.is_demo ? " (demo)" : ""}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Measure</label>
              <select className="select" value={form.measure} onChange={(e) => update({ measure: e.target.value })} data-testid="kpi-measure">
                <option value="">— count rows —</option>
                {allFields.map((c) => <option key={c.name} value={c.name}>{c.display_name || c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Aggregation</label>
              <select className="select" value={form.aggregation} onChange={(e) => update({ aggregation: e.target.value })} data-testid="kpi-agg">
                {AGGREGATIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Prefix</label>
              <input className="input" value={form.number_format?.prefix || ""} onChange={(e) => update({ number_format: { ...form.number_format, prefix: e.target.value } })} />
            </div>
            <div>
              <label className="label">Suffix</label>
              <input className="input" value={form.number_format?.suffix || ""} onChange={(e) => update({ number_format: { ...form.number_format, suffix: e.target.value } })} />
            </div>
            <div>
              <label className="label">Decimals</label>
              <input className="input" type="number" value={form.number_format?.decimals ?? 0} onChange={(e) => update({ number_format: { ...form.number_format, decimals: Number(e.target.value) } })} />
            </div>
          </div>
          {error && <div className="text-[12.5px] px-3 py-2 rounded" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }}>{error}</div>}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2" style={{ borderColor: "var(--c-border)" }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !form.name || !form.dataset_id} data-testid="kpi-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
